const SESSION_ACQUISITION_KEY = 'ab.session_acquisition.v1';
const ENTRY_PAGE_FLAG_KEY = 'ab.entry_page_viewed.v1';
const LAST_COMPATIBILITY_KEY = 'ab.last_compatibility.v1';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

const PAGE_SOURCE_TYPES = {
  product_comparison: 'product_comparison',
  upgrade_comparison: 'upgrade_comparison',
  product_pdp: 'product_pdp',
  compatibility_page: 'compatibility',
  homepage: 'homepage',
  seasonal_launch: 'seasonal_launch',
};

let pageViewId = 0;
let pageViewPath = '';
const claimedKeys = new Set();

function canUseSessionStorage() {
  try {
    return typeof sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

function readJson(key) {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / quota must not break the page.
  }
}

export function getInboundUtmContext(search) {
  const raw =
    search ??
    (typeof window !== 'undefined' && window.location
      ? window.location.search
      : '');
  const query = String(raw || '');
  const params = new URLSearchParams(
    query.startsWith('?') ? query.slice(1) : query
  );
  const context = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value != null && String(value).trim() !== '') {
      context[key] = String(value).trim();
    }
  }
  return context;
}

export function getReferrerHost(referrer, currentHost) {
  const raw = String(referrer || '').trim();
  if (!raw) return '';
  try {
    const host = new URL(raw).hostname.replace(/^www\./i, '').toLowerCase();
    const local = String(currentHost || '')
      .replace(/^www\./i, '')
      .toLowerCase();
    if (!host || (local && host === local)) return '';
    return host;
  } catch {
    return '';
  }
}

export function classifyTrafficSource(utm = {}, referrerHost = '') {
  if (utm.utm_source || utm.utm_medium || utm.utm_campaign) return 'campaign';
  if (referrerHost) return 'referral';
  return 'direct';
}

export function classifyPageType(path = '', pageSource = '') {
  if (pageSource && PAGE_SOURCE_TYPES[pageSource]) {
    return PAGE_SOURCE_TYPES[pageSource];
  }
  const route = String(path || '');
  if (route === '/' || route === '') return 'homepage';
  if (route.startsWith('/comparisons/lvs34-vs-')) return 'upgrade_comparison';
  if (route.startsWith('/comparisons/')) return 'product_comparison';
  if (route.startsWith('/simple-products/')) return 'product_pdp';
  if (route === '/compatibility') return 'compatibility_landing';
  if (route.startsWith('/compatibility/')) return 'compatibility';
  if (route === '/compare') return 'compare_hub';
  if (route.startsWith('/ice-fishing/')) return 'seasonal_launch';
  return '';
}

function currentPathname() {
  return typeof window !== 'undefined' && window.location
    ? window.location.pathname
    : '';
}

function currentSearch() {
  return typeof window !== 'undefined' && window.location
    ? window.location.search
    : '';
}

function currentHostname() {
  return typeof window !== 'undefined' && window.location
    ? window.location.hostname
    : '';
}

function currentReferrer() {
  return typeof document !== 'undefined' ? document.referrer : '';
}

export function captureSessionAcquisition({
  path,
  search,
  referrer,
  currentHost,
} = {}) {
  const existing = getSessionAcquisition();
  if (existing) return existing;

  const utm = getInboundUtmContext(search ?? currentSearch());
  const referrerHost = getReferrerHost(
    referrer ?? currentReferrer(),
    currentHost ?? currentHostname()
  );
  const record = {
    landing_path: path || currentPathname() || '/',
    traffic_source: classifyTrafficSource(utm, referrerHost),
    ...utm,
  };
  if (referrerHost) record.referrer_host = referrerHost;
  writeJson(SESSION_ACQUISITION_KEY, record);
  return record;
}

export function getSessionAcquisition() {
  return readJson(SESSION_ACQUISITION_KEY);
}

export function getSessionAcquisitionFields() {
  const acquisition = getSessionAcquisition();
  if (!acquisition) return {};
  const fields = {};
  for (const key of UTM_KEYS) {
    if (acquisition[key]) fields[key] = acquisition[key];
  }
  if (acquisition.landing_path) fields.landing_path = acquisition.landing_path;
  if (acquisition.traffic_source) fields.traffic_source = acquisition.traffic_source;
  if (acquisition.referrer_host) fields.referrer_host = acquisition.referrer_host;
  return fields;
}

export function rememberCompatibilityDecision(decision = {}) {
  const displaySlug = decision.displaySlug || decision.display_slug;
  const livescopeSlug = decision.livescopeSlug || decision.livescope_slug;
  const result = decision.result;
  if (!displaySlug || !livescopeSlug || !result) return null;
  const record = {
    compatibility_display_slug: String(displaySlug),
    compatibility_livescope_slug: String(livescopeSlug),
    compatibility_result: String(result),
  };
  if (decision.compatibilityId || decision.compatibility_id) {
    record.compatibility_id = String(
      decision.compatibilityId || decision.compatibility_id
    );
  }
  if (decision.verificationState || decision.verification_state) {
    record.compatibility_verification_state = String(
      decision.verificationState || decision.verification_state
    );
  }
  writeJson(LAST_COMPATIBILITY_KEY, record);
  return record;
}

export function getLastCompatibilityDecision() {
  return readJson(LAST_COMPATIBILITY_KEY);
}

export function hasRecordedEntryPageView() {
  if (!canUseSessionStorage()) return false;
  try {
    return sessionStorage.getItem(ENTRY_PAGE_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function markEntryPageViewRecorded() {
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.setItem(ENTRY_PAGE_FLAG_KEY, '1');
  } catch {
    // Ignore storage failures.
  }
}

export function markAnalyticsPageView(path) {
  const nextPath = String(path || currentPathname() || '/');
  if (pageViewPath === nextPath && pageViewId > 0) return pageViewId;
  pageViewPath = nextPath;
  pageViewId += 1;
  return pageViewId;
}

export function getAnalyticsPageViewId() {
  return pageViewId;
}

export function claimAnalyticsOnce(key) {
  const token = String(key || '');
  if (!token) return false;
  if (claimedKeys.has(token)) return false;
  claimedKeys.add(token);
  return true;
}

export function createOutboundClickId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to getRandomValues / Math.random.
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function isNonPiiClickId(value) {
  const clickId = String(value || '');
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(clickId)) {
    return false;
  }
  return !/@|email|name|phone|address/i.test(clickId);
}

export function resetAttributionStateForTests() {
  claimedKeys.clear();
  pageViewId = 0;
  pageViewPath = '';
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.removeItem(SESSION_ACQUISITION_KEY);
    sessionStorage.removeItem(ENTRY_PAGE_FLAG_KEY);
    sessionStorage.removeItem(LAST_COMPATIBILITY_KEY);
  } catch {
    // Ignore storage failures in tests.
  }
}
